/** * 担当者編集コンポーネント */
<template>
  <ifield-b-modal
    id="schedule-assign-user-editor-dialog"
    ref="modal"
    v-bind:title="$t('theScheduleAssignUserEditor.editAssignees')"
    centered
    scrollable
    no-body-border
    size="lg"
    ok-variant="success"
    ignore-enforce-focus-selector=".popover"
    v-bind:ok-title="$t('theScheduleAssignUserEditor.action.update')"
    v-bind:cancel-title="$t('theScheduleAssignUserEditor.action.cancel')"
    v-bind:ok-disabled="!isAssignUserChange"
    v-on:ok.prevent="$_saveSchedule"
  >
    <loading-mask isFullScreen v-bind:isLoading="isLoading" />
    <!-- フォームグループ(本体) -->
    <form-group
      ref="form"
      v-bind:id="'schedule-assign-user-form'"
      v-bind:onIfieldApiError="handleApiError"
      v-on:form-group-value-change="$_onFormValueChange"
    >
      <template v-slot:assign_users="{ idPrefix, value, state, setValue }">
        <!-- 担当者選択 -->
        <template v-if="isManager">
          <schedule-assign-user-selector
            v-bind:users="extendTargetUsers"
            v-bind:id-prefix="idPrefix"
            v-bind:value="value"
            v-bind:state="state"
            v-bind:show-assign-me-link="isTargetUser"
            v-on:assigned-users-selectend="setValue"
          />
        </template>
        <!-- 自分が担当になる or 担当から外れる場合の対象ユーザー名 -->
        <template v-else>
          <p class="fixed-user">{{ fixedUserLabel }}</p>
        </template>
      </template>
      <!-- コメント -->
      <template v-slot:assign_message="{ setValue }">
        <b-form-textarea
          class="assign-comment"
          no-resize
          trim
          rows="4"
          max-rows="10"
          v-bind:placeholder="
            $t('theScheduleAssignUserEditor.placeholder.detail')
          "
          v-on:change="setValue"
        />
        <p
          class="assign-comment-notes"
          v-t="'theScheduleAssignUserEditor.message.sendToAllTargetUsers'"
        />
      </template>
    </form-group>
  </ifield-b-modal>
</template>

<script>
import { RestApiClient } from 'ifield-core-components'
import { mapMutations } from 'vuex'
import Schedule from '@/model/Schedule.js'
import ScheduleAssignUserSelector from '@/components/schedule/AppScheduleAssignUserSelector.vue'
import { ATTRIBUTE_TYPE } from 'ifield-core-components'
import { toLocalTz, toLocalTzAttribute, toServerTz } from '@/js/TimezoneHelper'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import _ from 'lodash'

/**
 * 項目名一覧
 */
const FIELD_NAME = {
  // 説明
  ASSIGN_MESSAGE: 'assign_message',
  // 担当者
  ASSIGN_USERS: 'assign_users',
}

export default {
  name: 'theScheduleAssignUserEditor',
  components: {
    'schedule-assign-user-selector': ScheduleAssignUserSelector,
  },
  props: {
    /**
     * スケジュールデータ
     */
    schedule: {
      type: Schedule,
      required: true,
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      // ロード中か？
      isLoading: false,
      // 担当者として選択されているユーザーID
      selectedUserIds: [],
    }
  },
  computed: {
    /**
     * ログインユーザーのID
     * @type {Number}
     */
    loginUserId: function () {
      return this.$store.state.login.userId
    },
    /**
     * 項目ラベルMap(key: 項目名, value: 表示ラベル)
     * @type {Map}
     */
    fieldLabelMap: function () {
      return new Map([
        [
          FIELD_NAME.ASSIGN_USERS,
          this.$t('theScheduleAssignUserEditor.field.assignees'),
        ],
        [
          FIELD_NAME.ASSIGN_MESSAGE,
          this.$t('theScheduleAssignUserEditor.field.comment'),
        ],
      ])
    },
    /**
     * 対象ユーザー(グループの所属ユーザーを含む)
     * @type {Object[]}
     */
    extendTargetUsers: function () {
      return this.schedule?.extendTargetUsers || []
    },
    /**
     * 担当者ユーザーID
     * @type {Array<Number>}
     */
    assignedUserIds: function () {
      return this.schedule && this.schedule.assignedUserIds
    },
    /**
     * 対象ユーザー選択フィールド名称
     * @type {String}
     */
    targetUsersFieldName: function () {
      return FIELD_NAME.TARGET_USERS
    },
    /**
     * ログインユーザーが管理者の場合はtrue
     * @type {Boolean}
     */
    isManager: function () {
      return this.$store.state.login.isManager
    },
    /**
     * ログインユーザーが対象ユーザーか判定
     * @type {Boolean}
     */
    isTargetUser: function () {
      return this.schedule && this.schedule.isTargetUser(this.loginUserId)
    },
    /**
     * 一般権限ユーザーの担当者ID
     * @type {Array.<Number>}
     */
    fixedUserIds: function () {
      const isAssignedMe =
        this.schedule && this.schedule.isAssigned(this.loginUserId)
      if (isAssignedMe) {
        // 現在のユーザーは含まれている -> 削除処理
        return this.schedule.assignedUserIds.filter(
          (id) => id !== this.loginUserId
        )
      } else {
        // 現在のユーザーは含まれていない -> 追加処理
        return [...this.schedule.assignedUserIds, this.loginUserId].sort(
          (a, b) => a - b
        )
      }
    },
    /**
     * 一般権限ユーザーの担当者変更時文字列
     * @type {String}
     */
    fixedUserLabel: function () {
      if (!this.schedule || !this.fixedUserIds.length) return '-'
      return this.fixedUserIds
        .map((id) => {
          const user = this.extendTargetUsers.find((u) => u.user_id === id)
          return user.user_name
        })
        .join(',')
    },
    /**
     * フォームデータ
     * @type {Object[]}
     */
    forms: function () {
      return [
        // 担当者
        {
          fieldName: FIELD_NAME.ASSIGN_USERS,
          label: this.fieldLabelMap.get(FIELD_NAME.ASSIGN_USERS),
          attributeType: ATTRIBUTE_TYPE.SLOT,
          value: this.isManager ? this.assignedUserIds : this.fixedUserIds,
          disabled: !this.isManager,
        },
        // コメント
        {
          fieldName: FIELD_NAME.ASSIGN_MESSAGE,
          label: this.fieldLabelMap.get(FIELD_NAME.ASSIGN_MESSAGE),
          attributeType: ATTRIBUTE_TYPE.SLOT,
          value: '',
          placeholder: this.$t(
            'theScheduleAssignUserEditor.placeholder.detail'
          ),
        },
      ]
    },
    /**
     * 担当者の選択に変更がある場合はtrue
     * @type {Boolean}
     */
    isAssignUserChange: function () {
      return !_.isEqual(
        [...this.selectedUserIds].sort((a, b) => a - b),
        this.assignedUserIds
      )
    },
  },
  mounted: function () {
    this.$log.info('TheScheduleAssignUserEditor mounted.')
  },
  methods: {
    ...mapMutations({
      /**
       * 保存したスケジュール内容をstoreに格納する
       */
      $_registerLastSavedSchedule: 'updateScheduleLastSaved',
    }),

    /**
     * ダイアログを表示する
     * @public
     */
    showModal: function () {
      this.$_initializeSelectedUserIds()
      this.$refs['modal'].show()
      this.$nextTick(() => {
        this.$_setForms()
      })
    },

    /**
     * 選択値を初期化する
     */
    $_initializeSelectedUserIds: function () {
      this.selectedUserIds = this.isManager
        ? [...this.schedule.assignedUserIds]
        : [...this.fixedUserIds]
    },

    /**
     * ダイアログを非表示にする
     * @public
     */
    hideModal: function () {
      this.$refs['modal'].hide()
    },

    /**
     * フォームにフォームデータを設定する
     */
    $_setForms: function () {
      const form = this.$refs['form']
      form?.setData(this.forms)
    },

    /**
     * フォーム値変更時処理
     * @param {String} fieldName 対象フィールド名
     * @param {*} value 変更値
     */
    $_onFormValueChange: function (fieldName, value) {
      if (fieldName === FIELD_NAME.ASSIGN_USERS) {
        this.selectedUserIds = value || []
      }
    },

    /**
     * スケジュールを保存する
     */
    $_saveSchedule: async function () {
      this.$_showLoading()

      // フォームから値を取得
      let values
      try {
        values = this.$refs['form'].getValues()
      } catch (error) {
        this.$log.info(error)
        this.$_hideLoading()
        return
      }

      // リクエストボディ作成
      const requestBody = this.$_makeRequestBody(values)

      // API実行
      try {
        await this.$_putSchedule(requestBody)
      } catch (error) {
        if (error.status === 409) {
          /**
           * スケジュール担当者保存失敗イベント
           */
          this.$emit('assign-user-editfailed')
          this.hideModal()
        }
        this.$_hideLoading()
        return
      }

      const savedSchedule = await this.$_getSchedule(this.schedule.id)
      if (savedSchedule) {
        // storeに保存
        this.$_registerLastSavedSchedule(savedSchedule)
      }

      this.$_showSavedToast()
      /**
       * スケジュール担当者保存完了イベント
       */
      this.$emit('assign-user-editend')
      this.hideModal()
      this.$_hideLoading()
    },

    /**
     * リクエストボディを作成する
     *
     * @param {Array.<Object>} values フォームから取得した値
     * @returns {Object} リクエストボディ
     */
    $_makeRequestBody: function (values) {
      const body = this.schedule.toRequestJSON()
      // 受け取ったスケジュールデータのうち再送する値をサーバータイムゾーンに戻す
      if (!body.allday_flg) {
        body.start_at = toServerTz(body.start_at)
        body.end_at = toServerTz(body.end_at)
      }
      for (const attr of body.attributes) {
        const attributeType = this.schedule.attributes.find(
          (a) => a.attribute_id === attr.attribute_id
        )?.attribute_type
        if (attributeType == ATTRIBUTE_TYPE.DATETIME && attr.attribute_value) {
          attr.attribute_value = toServerTz(attr.attribute_value)
        }
      }

      for (const item of values) {
        if (item.name === FIELD_NAME.ASSIGN_USERS) {
          const assignUserIds = item.value || []
          this.$_applyToTargetUsers(body.target_users, assignUserIds)
        } else if (item.name === FIELD_NAME.ASSIGN_MESSAGE) {
          body.assign_message = item.value
        }
      }

      return body
    },

    /**
     * 担当の指定を targetUsers に反映する
     * in-place で適用します
     * @param {Object[]} targetusers body の target_users (in-place で反映される)
     * @param {number[]} assignUserIds 担当に指定されたユーザーのID
     */
    $_applyToTargetUsers: function (targetUsers, assignUserIds) {
      const assignUserIdSet = new Set(assignUserIds)

      // 以下のユーザーを target_users に追加する
      // * 担当者として指定されている
      // * target_users に存在しない
      const targetUserIdSet = new Set(this.schedule.targetUserIds)
      const userIdsToAppendTarget = assignUserIds.filter(
        (id) => !targetUserIdSet.has(id)
      )
      for (const id of userIdsToAppendTarget) {
        targetUsers.push({
          user_id: id,
          assign_flg: true,
        })
      }

      // 以下のユーザーを target_users から外す
      // * 担当者として指定されていない
      // * target_users と target_organizations の所属ユーザーの両方に存在する
      const targetUserIdsNotAssign = this.schedule.targetUserIds.filter(
        (id) => !assignUserIdSet.has(id)
      )
      const targetUserIdsNotAssignAlsoInOrganization =
        targetUserIdsNotAssign.filter((id) =>
          this.schedule.targetOrganizations.some((o) =>
            o.organization_user_relations.find((u) => u.user_id === id)
          )
        )
      for (const id of targetUserIdsNotAssignAlsoInOrganization) {
        const index = targetUsers.findIndex((u) => u.user_id === id)
        index >= 0 && targetUsers.splice(index, 1)
      }

      // 既存ユーザーも含めたフラグ設定
      for (const user of targetUsers) {
        user.assign_flg = assignUserIdSet.has(user.user_id)
      }
    },

    /**
     * スケジュールをPUTする
     * @param {Object} requestBody リクエストボディ
     */
    $_putSchedule: async function (requestBody) {
      const url = RestApiClient.createApiUrl(`schedule/${this.schedule.id}`)
      try {
        return await RestApiClient.executePutApi(url, requestBody)
      } catch (error) {
        this.$_hideLoading()
        await this.handleApiError(error)
        throw error
      }
    },

    /**
     * 単一のスケジュールを取得する
     *
     * @param {Number} id スケジュールID
     * @returns {Promise.<Object>}
     */
    $_getSchedule: async function (id) {
      const url = RestApiClient.createApiUrl(`schedule/${+id}`)

      let d
      try {
        d = await RestApiClient.executeGetApi(url).then(({ data }) => data)
      } catch (error) {
        this.handleApiError(error, null)
        return
      }

      return {
        ...d,
        start_at: d.allday_flg ? d.start_at : toLocalTz(d.start_at),
        end_at: d.allday_flg ? d.end_at : toLocalTz(d.end_at),
        create_at: toLocalTz(d.create_at),
        update_at: toLocalTz(d.update_at),
        attributes: d.attributes.map((attr) => toLocalTzAttribute(attr)),
      }
    },

    /**
     * 保存完了トーストを表示する
     */
    $_showSavedToast: function () {
      this.$toast.fire({
        position: 'top',
        icon: 'success',
        title: this.$t('theScheduleAssignUserEditor.message.updated'),
        confirmButtonText: this.$t('theScheduleAssignUserEditor.action.ok'),
        confirmButtonColor: '#6c757d',
        timer: 2000,
      })
    },

    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isLoading = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.fixed-user {
  /* ラベルと高さを合わせる  */
  margin: 0.375em 0 0 0;
}

.assign-comment {
  @include mixin-scrollbar;
  &::-webkit-scrollbar-track {
    border-top-right-radius: $border-radius-small;
    border-bottom-right-radius: $border-radius-small;
  }
}

.assign-comment-notes {
  font-size: $font-size-small;
  margin: 8px 0 0 0;
  color: #707070;
}
</style>
